<template>
  <div class="task-sync-mapping">
    <BaseAlert v-if="loading" state="info"
      >Loading database properties…</BaseAlert
    >
    <template v-else>
      <FormInputGroup
        label="Task completion"
        for-id="task-sync-status-property"
        help="Select, status and checkbox properties can drive Routine's task checkbox."
      >
        <BaseInputSelect
          select-only
          searchable
          display="form"
          :options="statusPropertyOptions"
          :value="statusPropertyId"
          placeholder="Do not map completion"
          @update:value="selectStatusProperty"
        />
      </FormInputGroup>

      <StatusValueMapping
        v-if="statusValueOptions.length"
        :model-value="values"
        :options="statusValueOptions"
        :property-name="selectedProperty?.name ?? ''"
        @update:model-value="setValues"
      />

      <FormInputGroup
        label="Planned for"
        for-id="task-sync-date-property"
        help="Only date properties can be mapped to the task's planned date."
      >
        <BaseInputSelect
          select-only
          searchable
          display="form"
          :options="datePropertyOptions"
          :value="datePropertyId"
          placeholder="Do not map a planned date"
          @update:value="selectDateProperty"
        />
      </FormInputGroup>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch, type PropType } from "vue";

import type { Cell } from "@ctrl/controller-rpc";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import FormInputGroup from "@/components/graph/definition/form/FormInputGroup.vue";
import {
  emptyStatusValueMapping,
  valuesReadAs,
  type StatusValueMappingState,
} from "@/components/settings/status-value-mapping";
import StatusValueMapping from "@/components/settings/StatusValueMapping.vue";
import type { TaskStatus } from "@/components/task/TaskCheckbox.vue";

import {
  providerStatusOptions,
  type TaskSyncMapping,
  type TaskSyncMappingProperty,
} from "./useIntegrationSyncWorkflow";

const stringCell = (value: string): Cell => ({ type: "string", value });

const isComplete = (option: { group?: string }): boolean =>
  option.group?.toLowerCase() === "complete";

/**
 * Where a property's values land before the user touches anything: the
 * provider's own "complete" grouping when it has one, everything else to-do.
 * Discarded has no provider equivalent, so it writes the same value as done.
 */
export function defaultValuesFor(
  property: TaskSyncMappingProperty
): StatusValueMappingState {
  if (property.kind === "checkbox") {
    return {
      writes: { done: "true", todo: "false", discarded: "true" },
      reads: {},
    };
  }
  const reads: Record<string, TaskStatus> = {};
  for (const option of property.options) {
    reads[option.id] = isComplete(option) ? "done" : "todo";
  }
  const done = property.options.find(isComplete)?.id;
  const todo =
    property.options.find((option) => option.group?.toLowerCase() === "to-do")
      ?.id ?? property.options.find((option) => !isComplete(option))?.id;
  return {
    writes: {
      ...(done !== undefined ? { done, discarded: done } : {}),
      ...(todo !== undefined ? { todo } : {}),
    },
    reads,
  };
}

export default defineComponent({
  name: "TaskSyncMappingStep",
  components: {
    BaseAlert,
    BaseInputSelect,
    FormInputGroup,
    StatusValueMapping,
  },
  props: {
    properties: {
      type: Array as PropType<TaskSyncMappingProperty[]>,
      required: true,
    },
    modelValue: {
      type: Object as PropType<TaskSyncMapping>,
      required: true,
    },
    loading: { type: Boolean, required: true },
  },
  emits: {
    "update:modelValue": (_: TaskSyncMapping) => true,
  },
  setup(props, { emit }) {
    const statusPropertyId = ref(props.modelValue.statusPropertyId);
    const datePropertyId = ref(props.modelValue.datePropertyId);
    const values = ref<StatusValueMappingState>(emptyStatusValueMapping());

    const selectedProperty = computed(() =>
      props.properties.find(
        (property) => property.id === statusPropertyId.value
      )
    );

    const statusValueOptions = computed(() =>
      providerStatusOptions(selectedProperty.value)
    );

    const toOptions = (
      properties: TaskSyncMappingProperty[]
    ): SelectOption[] => [
      { value: "", label: "None", aliases: [] },
      ...properties.map((property) => ({
        value: property.id,
        label: property.name,
        aliases: [],
      })),
    ];

    const completionProperties = computed(() =>
      props.properties.filter((property) => property.kind !== "date")
    );

    const statusPropertyOptions = computed(() =>
      toOptions(completionProperties.value)
    );
    const datePropertyOptions = computed(() =>
      toOptions(props.properties.filter((property) => property.kind === "date"))
    );

    const emitMapping = () => {
      const property = selectedProperty.value;
      if (!property) {
        emit("update:modelValue", {
          statusPropertyId: "",
          datePropertyId: datePropertyId.value,
        });
        return;
      }

      const { todo, done, discarded } = values.value.writes;
      if (todo === undefined || done === undefined || discarded === undefined) {
        // Leaving statusCells out keeps the wizard's "Set up" disabled until
        // every status has a value to write.
        emit("update:modelValue", {
          statusPropertyId: property.id,
          datePropertyId: datePropertyId.value,
        });
        return;
      }

      const known = statusValueOptions.value.map((option) => option.value);
      const cellsFor = (status: TaskStatus) =>
        valuesReadAs(values.value, known, status).map(stringCell);

      emit("update:modelValue", {
        statusPropertyId: property.id,
        datePropertyId: datePropertyId.value,
        statusCells: {
          todo: { read: cellsFor("todo"), write: stringCell(todo) },
          done: { read: cellsFor("done"), write: stringCell(done) },
          discarded: {
            read: cellsFor("discarded"),
            write: stringCell(discarded),
          },
        },
      });
    };

    const selectStatusProperty = (id: string) => {
      statusPropertyId.value = id;
      const property = selectedProperty.value;
      values.value = property
        ? defaultValuesFor(property)
        : emptyStatusValueMapping();
      emitMapping();
    };

    const selectDateProperty = (id: string) => {
      datePropertyId.value = id;
      emitMapping();
    };

    const setValues = (next: StatusValueMappingState) => {
      values.value = next;
      emitMapping();
    };

    if (statusPropertyId.value) selectStatusProperty(statusPropertyId.value);

    // The step is mounted while the provider's properties are still loading, so
    // the defaults derived from them land after this setup ran.
    watch(
      () => props.modelValue,
      (mapping) => {
        if (
          mapping.statusPropertyId === statusPropertyId.value &&
          mapping.datePropertyId === datePropertyId.value
        ) {
          return;
        }
        datePropertyId.value = mapping.datePropertyId;
        selectStatusProperty(mapping.statusPropertyId);
      }
    );

    return {
      statusPropertyId,
      datePropertyId,
      values,
      completionProperties,
      selectedProperty,
      statusValueOptions,
      statusPropertyOptions,
      datePropertyOptions,
      selectStatusProperty,
      selectDateProperty,
      setValues,
    };
  },
});
</script>

<style scoped lang="scss">
.task-sync-mapping {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
